import React, { useEffect, useRef, useState } from 'react';
import DropdownMenuPortal from '../../tasks/WorkOrders/QuickTasks/components/DropdownMenuPortal';
import { getStatusVariant, statusPillStyles } from './inlineCellStyles';
import { ReclamationStatus } from '../models';

const getOptionStatusValue = (option) => {
  if (Array.isArray(option?.value)) return null;
  const value = Number(option?.value);
  return Number.isNaN(value) ? null : value;
};

const InlineStatusCell = ({ rowData, mixIns }) => {
  const statusOptions = mixIns?.statusOptions || [];
  const dropdownAlign = mixIns?.statusDropdownAlign || 'left';
  const dropdownMinWidth = mixIns?.statusDropdownMinWidth || 340;
  const currentStatusValue = rowData?._status;
  const [selectedValue, setSelectedValue] = useState(currentStatusValue);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    setSelectedValue(currentStatusValue);
    setOpen(false);
  }, [currentStatusValue]);

  if (!statusOptions.length) {
    return <span>{rowData?._statusLabel || rowData?.status || '-'}</span>;
  }

  const chooseOption = (optionValue) => {
    setOpen(false);
    const normalized = Number(optionValue);
    if (!Number.isNaN(normalized) && normalized !== selectedValue) {
      if (typeof mixIns?.onSaveStatus === 'function') {
        mixIns.onSaveStatus(rowData._id, normalized);
      }
    }
  };

  const selectedIndex = statusOptions.findIndex(
    (option) => getOptionStatusValue(option) === Number(selectedValue)
  );
  const selectedOption = selectedIndex >= 0 ? statusOptions[selectedIndex] : null;
  const fallbackLabel = rowData?._statusLabel || rowData?.status;
  const displayLabel = selectedOption?.label || fallbackLabel || '-';
  const statusKey =
    Object.keys(ReclamationStatus).find(
      (key) => Number(ReclamationStatus[key]) === Number(selectedValue)
    ) ||
    selectedOption?.rawKey ||
    fallbackLabel?.replace(/\s+/g, '');
  const customVariant = statusPillStyles.custom[statusKey];
  const variantStyle = customVariant || statusPillStyles.variants[getStatusVariant(statusKey)];

  // _allowedStatuses / _statusEditLocked come from the reclamation transformer, which
  // resolves them through validateStatusTransition. Never re-derive the rules here.
  const allowedStatuses = Array.isArray(rowData?._allowedStatuses) ? rowData._allowedStatuses : [];
  const disableStatusEdit = Boolean(rowData?._statusEditLocked) || allowedStatuses.length === 0;

  const toggleOpen = (event) => {
    event.stopPropagation();
    if (disableStatusEdit) return;
    setOpen((prev) => !prev);
  };

  const tooltipText = disableStatusEdit
    ? rowData?._statusEditLockReason || 'Status changes are not available for this claim.'
    : 'Click to edit status';

  const availableOptions = statusOptions.filter((option) =>
    allowedStatuses.includes(getOptionStatusValue(option))
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span title={tooltipText} style={{ display: 'inline-block' }}>
        <button
          type="button"
          className="status-pill-button"
          ref={buttonRef}
          onClick={toggleOpen}
          aria-haspopup="listbox"
          aria-expanded={open}
          style={{
            ...statusPillStyles.base,
            ...variantStyle,
            border: 'none',
            cursor: disableStatusEdit ? 'not-allowed' : 'pointer',
            opacity: disableStatusEdit ? 0.6 : 1,
          }}
          disabled={disableStatusEdit}
        >
          <span>{displayLabel}</span>
          {!disableStatusEdit && (
            <span
              aria-hidden="true"
              style={{
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '5px solid currentColor',
                display: 'inline-block',
                marginLeft: '0.45rem',
                opacity: 0.9,
              }}
            />
          )}
        </button>
      </span>
      <DropdownMenuPortal
        anchorRef={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
        align={dropdownAlign}
        minWidth={dropdownMinWidth}
        maxWidth={420}
      >
        {availableOptions.map((option) => {
          const optionValue = Number(option.value);
          const isActive = optionValue === Number(selectedValue);
          return (
            <button
              key={option.value}
              type="button"
              className={`dropdown-item status-pill-menu-item${isActive ? ' active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                chooseOption(optionValue);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </DropdownMenuPortal>
    </div>
  );
};

export default InlineStatusCell;
